/**
 * DAISY Chatbot - Main Entry Point
 *
 * This is the entry point for the refactored Lit Web Components application.
 * It initializes the chatbot and registers all custom elements.
 */

// Import stores
import { chatStore } from './stores/chat.store.js';
import { userStore } from './stores/user.store.js';

// Import services
import { authService } from './services/auth.service.js';
import { configService } from './config/environment.js';
import { logger } from './utils/logger.js';
import { ensureChatStorageContext } from './utils/storage.js';

// Import main component
import './components/daisy-chatbox.js';

/**
 * Initialize the DAISY chatbot application
 */
export async function initializeApp() {
    logger.info('DAISY Chatbot initializing...');

    try {
        // Load environment configuration
        await configService.load();
        logger.info('Configuration loaded');

        const chatbotContainer = document.getElementById('daisy-chatbot-root');
        // PublicAPI resolves the authoritative Configurator `isActive` state
        // before the BFF mints a browser session. This applies to every council
        // without a council-specific build or runtime-config flag.
        const authResult = await authService.initialize();
        if (authResult?.tenantAvailable === false) {
            if (chatbotContainer) {
                const chatbot = document.createElement('daisy-chatbox');
                chatbot.serviceUnavailable = true;
                chatbotContainer.appendChild(chatbot);
            }
            logger.warn('DAISY is unavailable for the selected council');
            return;
        }

        const chatContextResult = ensureChatStorageContext({
            tenantId: configService.get('TENANT_ID'),
            workflowId: configService.get('WORKFLOW_ID'),
            councilName: configService.getCouncilFromUrl()
        });
        chatStore.setStorageContext(chatContextResult.context, {
            reset: chatContextResult.changed
        });
        if (chatContextResult.changed) {
            logger.info('Tenant-bound chat state reset for active council context');
        }

        logger.info('Authentication initialized');

        // Initialize stores with any persisted data
        chatStore.loadFromStorage(chatContextResult.context);
        userStore.loadFromStorage();
        logger.info('State stores initialized');

        // Create and mount the chatbot component
        if (chatbotContainer) {
            const chatbot = document.createElement('daisy-chatbox');
            chatbotContainer.appendChild(chatbot);
            logger.info('Chatbot component mounted');
        } else {
            logger.warn('No #daisy-chatbot-root element found');
        }

        logger.info('DAISY Chatbot ready!');
    } catch (error) {
        logger.error('Failed to initialize DAISY Chatbot:', error);
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApp);
} else {
    initializeApp();
}

// Listen for visibility changes to reopen chatbot when iframe becomes visible again
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
        const chatbot = document.querySelector('daisy-chatbox');
        if (chatbot && !chatbot.isOpen) {
            chatbot.open();
        }
    }
});

// Listen for messages from parent window to open chatbot
window.addEventListener('message', (event) => {
    if (event.data === 'openChatbot') {
        const chatbot = document.querySelector('daisy-chatbox');
        if (chatbot) {
            chatbot.open();
        }
    }
});

// Export for external access
export { chatStore, userStore, authService, configService };
